import { post } from '@webapp-next/lib/request/webapp/http';

interface Search {
  everything?: {
    field: string;
    type: string;
    data: number | string;
  };
  [key: string]: any;
}

interface Sort {
  [key: string]: string;
}

interface QueryOptions {
  max?: number;
  sort?: Sort;
  /** Stops the page walk: a large scan otherwise runs on past its caller. */
  signal?: AbortSignal;
  /**
   * Opt-in backstop on the page walk, counted in pages of `max` rows. Left
   * unset the walk is unbounded, because the collections read through here
   * have no ceiling and a cap a large tenant can reach turns a complete scan
   * into a thrown error. Set it only where the caller knows its collection
   * stays well under the bound, and read tripping it as the offsets failing to
   * advance rather than as a large tenant: a partial page walk returned as a
   * whole one silently answers with the wrong rows.
   */
  maxPages?: number;
}

interface ResourceQueryParams {
  search?: Search;
  join?: any[];
  assoc?: any[];
  group?: any[];
  aggr?: any;
}

const defaultSearch = {
  everything: {
    field: 'Id',
    type: 'gt',
    data: 0,
  },
};

export async function resourceQuery(
  system: string,
  {
    search = defaultSearch,
    join = [],
    assoc = [],
    group = [],
    aggr = {},
  }: ResourceQueryParams = {},
  { max = 500, sort = { Id: 'desc' }, signal, maxPages }: QueryOptions = {}
): Promise<any[]> {
  const body = {
    max,
    sort,
    search,
    join,
    assoc,
    group,
    aggr,
  };

  const rows: any[] = [];

  for (let page = 0; maxPages === undefined || page < maxPages; page += 1) {
    const { data } = await post(
      `api/v1/resource/${system}/QUERY`,
      { ...body, start: page * max },
      signal ? { signal } : {}
    );

    // A payload that is not a page of rows carries no length to compare
    // against `max`, so there is no telling a walk that finished from one that
    // was cut short. Returning the rows gathered so far would answer a partial
    // scan as a whole one; callers read a rejection as a failed page walk.
    if (!Array.isArray(data)) {
      throw new Error(`${system} query returned a page that is not rows`);
    }

    rows.push(...data);

    if (data.length < max) {
      return rows;
    }
  }

  throw new Error(`${system} query did not complete within ${maxPages} pages`);
}
